/**
 * Generic helper that sorts guarantees by order, grouping dependent items
 * under their parent.
 *
 * @param {Array} items - The list of guarantee items to sort.
 * @param {Object} accessors - Functions to extract values from each item:
 *   @param {Function} accessors.getOrder - Returns the numeric order (or null).
 *   @param {Function} accessors.getDependsOn - Returns the parent guaranteeId (or null).
 *   @param {Function} accessors.getName - Returns the display name for alphabetical sort.
 *   @param {Function} [accessors.getGuaranteeId] - Returns the guaranteeId of the item.
 * @returns {Array} Sorted items with parent/dependent grouping.
 */
function sortGuaranteesByOrder(items, accessors) {
  if (!items || !items.length) return [];

  const { getOrder, getDependsOn, getName, getGuaranteeId } = accessors;

  function byName(a, b) {
    return (getName(a) || "").localeCompare(getName(b) || "");
  }

  const isRoot = (item) => getDependsOn(item) === null;

  const withOrder = items
    .filter((item) => isRoot(item) && getOrder(item) !== null)
    .sort((a, b) => getOrder(a) - getOrder(b));

  const withoutOrder = items
    .filter((item) => isRoot(item) && getOrder(item) === null)
    .sort(byName);

  const result = [];

  for (const parent of [...withOrder, ...withoutOrder]) {
    result.push(parent);
    const parentId = getGuaranteeId(parent);
    const dependents = items
      .filter((item) => getDependsOn(item) != null && getDependsOn(item) == parentId)
      .sort(byName);
    dependents.forEach((d) => result.push(d));
  }

  /* Safety: add any items not placed yet (orphans with unknown parent) */
  const placed = new Set(result.map((item) => getGuaranteeId(item)));
  items
    .filter((item) => !placed.has(getGuaranteeId(item)))
    .sort(byName)
    .forEach((item) => result.push(item));

  return result;
}

export default sortGuaranteesByOrder;



// WEBPACK FOOTER //
// ./src/utils/sortGuaranteesByOrder.js